import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { api } from '@/app/api';
import { loadOwnerProfile, updateOwnerProfile } from '@main/features/auth/ownerAuthSlice';
import { UiButton, UiInput } from '@shared/common/ui';

import { ProfileTurnoverWidget } from './ProfileTurnoverWidget';
import { ProfileExpensesWidget } from './ProfileExpensesWidget';
import { ProfilePaymentsWidget } from './ProfilePaymentsWidget';
import { ProfileNewClientsWidget } from './ProfileNewClientsWidget';

import './ProfilePage.scss';

const moneyFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat('ru-RU');

function getCurrentYearPeriod() {
  const year = new Date().getFullYear();
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

function formatMoney(value) {
  return moneyFormatter.format(Number(value) || 0);
}

function formatDeduction(value) {
  const amount = Number(value) || 0;
  return amount > 0 ? `−${formatMoney(amount)}` : formatMoney(0);
}

function formatPeriodLabel({ from, to }) {
  const formatDate = (value) => new Intl.DateTimeFormat('ru-RU').format(new Date(`${value}T00:00:00`));
  if (from && to) return `${formatDate(from)} — ${formatDate(to)}`;
  if (from) return `с ${formatDate(from)}`;
  if (to) return `по ${formatDate(to)}`;
  return 'за всё время';
}

export function ProfilePage() {
  const dispatch = useDispatch();
  const profile = useSelector((s) => s.ownerAuth.profile);
  const [saveState, setSaveState] = useState(null); // { type: 'success'|'error', text }
  const [copyState, setCopyState] = useState(null); // { type: 'success'|'error', text }
  const [telegramLink, setTelegramLink] = useState(null);
  const [telegramLinkError, setTelegramLinkError] = useState(null);
  const [telegramLinkLoading, setTelegramLinkLoading] = useState(false);
  const [telegramAccountsLoading, setTelegramAccountsLoading] = useState(false);
  const [telegramAccountsState, setTelegramAccountsState] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [statisticsLoading, setStatisticsLoading] = useState(true);
  const [statisticsError, setStatisticsError] = useState(null);
  const [statisticsPeriod, setStatisticsPeriod] = useState(getCurrentYearPeriod);
  const [appliedStatisticsPeriod, setAppliedStatisticsPeriod] = useState(getCurrentYearPeriod);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    defaultValues: { fio: '', birthday: '' },
  });

  useEffect(() => {
    void dispatch(loadOwnerProfile());
  }, [dispatch]);

  const loadStatistics = useCallback(async () => {
    setStatisticsLoading(true);
    setStatisticsError(null);
    try {
      const { data } = await api.get('/owner/profile/statistics', {
        params: {
          ...(appliedStatisticsPeriod.from ? { from: appliedStatisticsPeriod.from } : {}),
          ...(appliedStatisticsPeriod.to ? { to: appliedStatisticsPeriod.to } : {}),
        },
      });
      setStatistics(data.statistics);
    } catch (err) {
      setStatisticsError(err.response?.data?.message ?? 'Не удалось загрузить статистику');
    } finally {
      setStatisticsLoading(false);
    }
  }, [appliedStatisticsPeriod]);

  useEffect(() => {
    void loadStatistics();
  }, [loadStatistics]);

  const applyStatisticsPeriod = (event) => {
    event.preventDefault();
    if (statisticsPeriod.from && statisticsPeriod.to && statisticsPeriod.from > statisticsPeriod.to) {
      setStatisticsError('Дата начала не может быть позже даты окончания');
      return;
    }
    setAppliedStatisticsPeriod({ ...statisticsPeriod });
  };

  const resetStatisticsPeriod = () => {
    const emptyPeriod = { from: '', to: '' };
    setStatisticsPeriod(emptyPeriod);
    setAppliedStatisticsPeriod(emptyPeriod);
  };

  useEffect(() => {
    if (profile) {
      reset({
        fio: profile.fio ?? '',
        birthday: profile.birthday ?? '',
      });
    }
  }, [profile, reset]);

  useEffect(() => {
    if (!telegramLink) return undefined;
    const refresh = () => {
      if (!isDirty) void dispatch(loadOwnerProfile());
    };
    const intervalId = window.setInterval(refresh, 5000);
    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId);
      setTelegramLink(null);
    }, telegramLink.expiresIn * 1000);
    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [dispatch, isDirty, telegramLink]);

  useEffect(() => {
    if (!telegramLink) return;
    const accountsCount = profile?.telegramAccounts?.length ?? 0;
    if (accountsCount > telegramLink.accountsCount) {
      setTelegramLink(null);
      setTelegramAccountsState({ type: 'success', text: 'Telegram-аккаунт привязан' });
    }
  }, [profile?.telegramAccounts?.length, telegramLink]);

  if (!profile) return <div className="placeholder">Загрузка профиля…</div>;

  const crmUrl =
    typeof window === 'undefined'
      ? `/${profile.id}/crm`
      : `${window.location.origin}/${profile.id}/crm`;

  const copyCrmUrl = async () => {
    setCopyState(null);
    try {
      await navigator.clipboard.writeText(crmUrl);
      setCopyState({ type: 'success', text: 'Ссылка скопирована' });
    } catch {
      setCopyState({ type: 'error', text: 'Не удалось скопировать ссылку' });
    }
  };

  const onSubmit = async (values) => {
    setSaveState(null);
    const payload = {
      fio: values.fio.trim() || null,
      birthday: values.birthday || null,
    };
    const result = await dispatch(updateOwnerProfile(payload));
    if (updateOwnerProfile.fulfilled.match(result)) {
      setSaveState({ type: 'success', text: 'Профиль сохранён' });
    } else {
      setSaveState({ type: 'error', text: result.payload ?? 'Ошибка сохранения' });
    }
  };

  const createTelegramLink = async () => {
    setTelegramLinkLoading(true);
    setTelegramLinkError(null);
    try {
      const { data } = await api.post('/owner/profile/telegram-link');
      setTelegramLink({
        ...data.link,
        accountsCount: profile.telegramAccounts?.length ?? 0,
      });
    } catch (err) {
      setTelegramLinkError(err.response?.data?.message ?? 'Не удалось создать код привязки');
    } finally {
      setTelegramLinkLoading(false);
    }
  };

  const refreshTelegramAccounts = async () => {
    setTelegramAccountsLoading(true);
    setTelegramAccountsState(null);
    const result = await dispatch(loadOwnerProfile());
    if (loadOwnerProfile.rejected.match(result)) {
      setTelegramAccountsState({ type: 'error', text: result.payload ?? 'Не удалось обновить список' });
    }
    setTelegramAccountsLoading(false);
  };

  const deleteTelegramAccount = async (account) => {
    const accountName = [account.firstName, account.lastName].filter(Boolean).join(' ')
      || (account.username ? `@${account.username}` : account.chatId);
    if (!window.confirm(`Отвязать Telegram-аккаунт «${accountName}»?`)) return;

    setTelegramAccountsLoading(true);
    setTelegramAccountsState(null);
    try {
      await api.delete(`/owner/profile/telegram-accounts/${encodeURIComponent(account.id)}`);
      const result = await dispatch(loadOwnerProfile());
      if (loadOwnerProfile.rejected.match(result)) {
        throw new Error(result.payload ?? 'Не удалось обновить профиль');
      }
      setTelegramAccountsState({ type: 'success', text: 'Telegram-аккаунт отвязан' });
    } catch (err) {
      setTelegramAccountsState({
        type: 'error',
        text: err.response?.data?.message ?? err.message ?? 'Не удалось отвязать Telegram-аккаунт',
      });
    } finally {
      setTelegramAccountsLoading(false);
    }
  };

  const telegramAccounts = profile.telegramAccounts ?? [];
  const telegramLinkButtonLabel = telegramLinkLoading
    ? 'Создаём…'
    : telegramAccounts.length > 0
      ? 'Добавить аккаунт'
      : 'Создать код привязки';
  const formatTelegramLinkedAt = (value) => {
    if (!value) return '—';
    return new Intl.DateTimeFormat('ru-RU', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(value));
  };

  return (
    <section>
      <div className="page-header">
        <h1>Профиль</h1>
      </div>

      <div className="form-card form-card--wide profile-statistics">
        <div className="profile-statistics__header">
          <div>
            <h2>Общая статистика</h2>
            <p className="muted">
              Все филиалы и продажи билетов · {formatPeriodLabel(appliedStatisticsPeriod)}
            </p>
          </div>
          <UiButton
            type="button"
            variant="ghost"
            size="sm"
            disabled={statisticsLoading}
            onClick={loadStatistics}
          >
            {statisticsLoading ? 'Обновляем…' : 'Обновить'}
          </UiButton>
        </div>

        <form className="profile-statistics__filters" onSubmit={applyStatisticsPeriod}>
          <UiInput
            label="Дата начала"
            type="date"
            value={statisticsPeriod.from}
            max={statisticsPeriod.to || undefined}
            onChange={(event) => setStatisticsPeriod((current) => ({
              ...current,
              from: event.target.value,
            }))}
          />
          <UiInput
            label="Дата окончания"
            type="date"
            value={statisticsPeriod.to}
            min={statisticsPeriod.from || undefined}
            onChange={(event) => setStatisticsPeriod((current) => ({
              ...current,
              to: event.target.value,
            }))}
          />
          <div className="profile-statistics__filter-actions">
            <UiButton type="submit" disabled={statisticsLoading}>Применить</UiButton>
            <UiButton
              type="button"
              variant="ghost"
              disabled={statisticsLoading && !statistics}
              onClick={resetStatisticsPeriod}
            >
              За всё время
            </UiButton>
          </div>
        </form>

        {statisticsError && (
          <div className="form-note form-note--error">{statisticsError}</div>
        )}

        {statisticsLoading && !statistics ? (
          <div className="placeholder">Считаем доход…</div>
        ) : statistics ? (
          <>
            <div className="profile-statistics__metrics">
              <div className="profile-statistics__metric">
                <span>Валовый доход</span>
                <strong>{formatMoney(statistics.totals.grossRevenue)}</strong>
                <small>До комиссий и налогов</small>
              </div>
              <div className="profile-statistics__metric">
                <span>Продажи билетов</span>
                <strong>{formatMoney(statistics.totals.ticketRevenue)}</strong>
                <small>
                  {integerFormatter.format(statistics.totals.ticketsSold)} билетов ·{' '}
                  {integerFormatter.format(statistics.totals.ticketOrders)} заказов
                </small>
              </div>
              <div className="profile-statistics__metric profile-statistics__metric--expense">
                <span>Комиссии и налог</span>
                <strong>{formatDeduction(statistics.totals.qrFee + statistics.totals.ticketTax)}</strong>
                <small>
                  QR 0,7%: {formatMoney(statistics.totals.qrFee)} · УСН 6% с билетов:{' '}
                  {formatMoney(statistics.totals.ticketTax)}
                </small>
              </div>
              <div className="profile-statistics__metric profile-statistics__metric--net">
                <span>Чистая сумма</span>
                <strong>{formatMoney(statistics.totals.netRevenue)}</strong>
                <small>После комиссии QR и налога с билетов</small>
              </div>
            </div>

            <div className="profile-statistics__breakdown">
              <h3>Доход по филиалам</h3>
              <div className="profile-statistics__table-wrap">
                <table className="data-table profile-statistics__table">
                  <thead>
                    <tr>
                      <th>Источник</th>
                      <th>Продажи</th>
                      <th>Валовый доход</th>
                      <th>QR-оборот</th>
                      <th>Комиссия QR</th>
                      <th>Чистая сумма</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statistics.branches.map((branch) => (
                      <tr key={branch.id}>
                        <td>{branch.name}</td>
                        <td>{integerFormatter.format(branch.successfulSales)}</td>
                        <td>{formatMoney(branch.grossRevenue)}</td>
                        <td>{formatMoney(branch.qrRevenue)}</td>
                        <td>{formatDeduction(branch.qrFee)}</td>
                        <td><strong>{formatMoney(branch.netRevenue)}</strong></td>
                      </tr>
                    ))}
                    <tr className="profile-statistics__ticket-row">
                      <td>Билеты</td>
                      <td>{integerFormatter.format(statistics.totals.ticketsSold)} шт.</td>
                      <td>{formatMoney(statistics.totals.ticketRevenue)}</td>
                      <td>{formatMoney(statistics.totals.ticketRevenue)}</td>
                      <td>
                        {formatDeduction(
                          statistics.totals.ticketRevenue * statistics.rates.qrFee,
                        )}
                      </td>
                      <td>
                        <strong>
                          {formatMoney(
                            statistics.totals.ticketRevenue
                              * (1 - statistics.rates.qrFee - statistics.rates.ticketTax),
                          )}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="profile-statistics__note">
                В чистой сумме учтена комиссия 0,7% только для QR-платежей. Для всех
                билетов дополнительно вычтен налог УСН 6%.
              </p>
            </div>
          </>
        ) : null}
      </div>

      <ProfileTurnoverWidget />
      <ProfileExpensesWidget />
      <ProfilePaymentsWidget ownerId={profile.id} />

      <div className="profile-details-grid">
        <div className="form-card">
          <h2>Персональные данные</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-grid">
              <UiInput label="Email" type="email" value={profile.email} disabled />
              <UiInput
                label="ФИО"
                type="text"
                autoComplete="name"
                error={errors.fio?.message}
                {...register('fio', {
                  maxLength: { value: 255, message: 'Не больше 255 символов' },
                })}
              />
              <UiInput label="Дата рождения" type="date" {...register('birthday')} />
            </div>

            {saveState && (
              <div className={`banner banner--${saveState.type}`}>{saveState.text}</div>
            )}

            <div className="form-actions">
              <UiButton type="submit" disabled={!isDirty || isSubmitting}>
                {isSubmitting ? 'Сохраняем…' : 'Сохранить'}
              </UiButton>
            </div>
          </form>
        </div>

        <div className="form-card">
          <h2>Данные для CRM</h2>
          <div className="copy-field">
            <input className="copy-field__input" type="text" value={crmUrl} readOnly />
            <UiButton type="button" variant="ghost" onClick={copyCrmUrl}>
              Скопировать
            </UiButton>
          </div>
          {copyState && (
            <div className={`form-note form-note--${copyState.type}`}>{copyState.text}</div>
          )}
          <p className="muted">
            Вход, затем выбор филиала (если доступно несколько). Из кабинета проще: вкладка
            «Филиалы» → «Открыть CRM» сразу открывает нужный филиал.
          </p>
        </div>
      </div>

      <div className="profile-bottom-grid">
        <ProfileNewClientsWidget />
        <div className="form-card profile-telegram">
          <h2>Telegram-бот</h2>
          <p className="muted">
            Привязанные аккаунты получают оповещения. Чтобы добавить аккаунт, создайте
            одноразовый код, затем отправьте боту команду /link и укажите этот код.
          </p>

          {telegramAccounts.length > 0 ? (
            <div className="profile-telegram__table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Аккаунт</th>
                    <th>Username</th>
                    <th>Chat ID</th>
                    <th>Привязан</th>
                    <th style={{ width: 120 }} />
                  </tr>
                </thead>
                <tbody>
                  {telegramAccounts.map((account) => {
                    const name = [account.firstName, account.lastName].filter(Boolean).join(' ');
                    return (
                      <tr key={account.id}>
                        <td>{name || 'Telegram-аккаунт'}</td>
                        <td>{account.username ? `@${account.username}` : '—'}</td>
                        <td>{account.chatId}</td>
                        <td>{formatTelegramLinkedAt(account.linkedAt)}</td>
                        <td>
                          <UiButton
                            type="button"
                            variant="danger"
                            size="sm"
                            disabled={telegramAccountsLoading}
                            onClick={() => deleteTelegramAccount(account)}
                          >
                            Отвязать
                          </UiButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="form-note" style={{ marginBottom: 16 }}>
              Привязанных Telegram-аккаунтов пока нет.
            </div>
          )}

          <div className="form-actions">
            <UiButton
              type="button"
              variant="ghost"
              onClick={refreshTelegramAccounts}
              disabled={telegramAccountsLoading}
            >
              {telegramAccountsLoading ? 'Обновляем…' : 'Обновить список'}
            </UiButton>
            <UiButton
              type="button"
              variant="ghost"
              onClick={createTelegramLink}
              disabled={telegramLinkLoading}
            >
              {telegramLinkButtonLabel}
            </UiButton>
          </div>
          {telegramLink && (
            <div className="form-note form-note--success">
              Код: <strong>{telegramLink.code}</strong>. Действует {Math.round(telegramLink.expiresIn / 60)} минут.
            </div>
          )}
          {telegramLinkError && (
            <div className="form-note form-note--error">{telegramLinkError}</div>
          )}
          {telegramAccountsState && (
            <div className={`form-note form-note--${telegramAccountsState.type}`}>
              {telegramAccountsState.text}
            </div>
          )}
        </div>
      </div>

    </section>
  );
}
